// Which palette each wave paints, and which palette something on the page has once waves
// reach it. The palette data itself lives in field/palettes.ts, so a page with no waves (the
// 404) can use it without pulling in the engine.

import {
  gustReachesBox,
  parseSrgb,
  srgbToOklab,
  type Box,
  type GustWave,
  type Vec3,
} from "@bfly/engine";
import type { Theme } from "../page/theme.js";
import { PALETTES, themePalette } from "../field/palettes.js";

/** How different two palettes have to be for a wave from one to the other to show, as an
    OKLab distance (`paletteDistance()`). Some palettes are near twins in one theme (the dark
    forms of sea and lagoon are 0.006 apart), and a wave between them looked like nothing
    happened. At 0.06, every palette still has at least 17 choices in either theme. */
export const PALETTE_DISTANCE_MIN = 0.06;

// The mean distance from each color of `a` to its nearest color in `b`.
function meanNearest(a: readonly Vec3[], b: readonly Vec3[]): number {
  let sum = 0;
  for (const [l, x, y] of a) {
    let nearest = Number.POSITIVE_INFINITY;
    for (const [bl, bx, by] of b) nearest = Math.min(nearest, Math.hypot(l - bl, x - bx, y - by));
    sum += nearest;
  }
  return a.length > 0 ? sum / a.length : 0;
}

// The distance between every pair of palettes in each theme, built on first use. Every new
// wave compares each palette with every one on screen, and the colors never change.
const distances: Record<Theme, Float64Array | null> = { light: null, dark: null };

function distanceTable(theme: Theme): Float64Array {
  const made = distances[theme];
  if (made) return made;
  const n = PALETTES.length;
  const forms = PALETTES.map((_, palette) =>
    themePalette(palette, theme).field.map((hex) => srgbToOklab(parseSrgb(hex))),
  );
  const table = new Float64Array(n * n);
  for (let a = 0; a < n; a++) {
    for (let b = a + 1; b < n; b++) {
      const distance = (meanNearest(forms[a]!, forms[b]!) + meanNearest(forms[b]!, forms[a]!)) / 2;
      table[a * n + b] = distance;
      table[b * n + a] = distance;
    }
  }
  distances[theme] = table;
  return table;
}

/** How different two palettes look in `theme`: the mean OKLab distance from each field color
    to the other palette's nearest one, averaged both ways. 0 for the same palette. A palette
    with no form in `theme` shows the first palette's form instead. */
export function paletteDistance(a: number, b: number, theme: Theme): number {
  const n = PALETTES.length;
  // An out-of-range index shows the first palette, the same as `themePalette()`.
  const shown = (index: number): number => (index >= 0 && index < n ? index : 0);
  return distanceTable(theme)[shown(a) * n + shown(b)] ?? 0;
}

/** A random palette with a form in `theme` that's different enough from every palette in
    `shown`. If that leaves nothing, only the newest one in `shown` counts. */
export function nextPalette(shown: readonly number[], theme: Theme, random: () => number): number {
  const unlike = (against: readonly number[]): number[] =>
    PALETTES.flatMap((palette, index) =>
      palette[theme] &&
      against.every((other) => paletteDistance(index, other, theme) >= PALETTE_DISTANCE_MIN)
        ? [index]
        : [],
    );
  let choices = unlike(shown);
  if (choices.length === 0) choices = unlike(shown.slice(-1));
  return choices[Math.floor(random() * choices.length)] ?? 0;
}

/** The base palette (outside every live painting wave), and the palette each live wave
    paints, in the same order as the rings (-1 means the wave keeps the colors). */
export interface Painting {
  readonly base: number;
  readonly waves: readonly number[];
}

/** The page's palette: the base, which `repaint()` changes all at once (the dust sweep),
    plus the palette of each painting wave, which paints as its front goes by.

    Anything on the page takes the palette of the newest wave whose front has passed it
    (`paletteAt()`). A wave that has ended has passed everything, so its palette becomes the
    base, and older waves can't show any more.

    The page doesn't start painting waves while the field's grains are off (`GUST_GRAINS` in
    field.ts). `start()` stays for when they're back. */
export class PaletteWaves {
  private base = 0;
  // Wave id to palette, oldest first (ids only go up).
  private readonly waves = new Map<number, number>();
  // The painting that `update()` fills in place each frame. Readers use it in the same
  // frame, or hold on to it as the latest (elements/jrsm-glyph-field.ts).
  private readonly painting: { base: number; waves: number[] } = { base: 0, waves: [] };

  constructor(private readonly random: () => number = Math.random) {}

  /** A wave that repaints the page. It picks a palette with a form in the page's theme that's
      different from every palette on screen (the base and each live wave's), so the change
      shows wherever it passes (`nextPalette()`). */
  start(id: number, theme: Theme): void {
    this.waves.set(id, nextPalette([this.base, ...this.waves.values()], theme, this.random));
  }

  /** Repaints the whole page at once, with a palette in the page's theme that's different
      from every palette on screen (`nextPalette()`). Any painting waves stop painting. */
  repaint(theme: Theme): void {
    this.base = nextPalette([this.base, ...this.waves.values()], theme, this.random);
    this.waves.clear();
  }

  /** The painting for the live waves, oldest first. A painting wave that isn't in `live` has
      ended. */
  update(live: readonly { readonly id: number }[]): Painting {
    let ended = -1;
    for (const id of this.waves.keys()) if (!isLive(live, id)) ended = Math.max(ended, id);
    if (ended >= 0) {
      this.base = this.waves.get(ended) ?? this.base;
      for (const id of this.waves.keys()) if (id <= ended) this.waves.delete(id);
    }
    const { painting } = this;
    painting.base = this.base;
    painting.waves.length = live.length;
    for (let i = 0; i < live.length; i++) painting.waves[i] = this.waves.get(live[i]!.id) ?? -1;
    return painting;
  }
}

function isLive(live: readonly { readonly id: number }[], id: number): boolean {
  for (const wave of live) if (wave.id === id) return true;
  return false;
}

/** A box's palette: the newest painting wave that has reached it, or else the base.
    `reaches` decides; by default it checks the fallback front at the box's nearest point, and
    the page passes one that reads the field's gust ages. The field's shader blends each
    wave's palette over the cells it reaches (`cellColor()`). */
export function paletteAt<Ring extends GustWave>(
  box: Box,
  rings: readonly Ring[],
  painting: Painting,
  reaches: (ring: Ring, box: Box) => boolean = gustReachesBox,
): number {
  let palette = painting.base;
  rings.forEach((ring, i) => {
    const wave = painting.waves[i] ?? -1;
    if (wave >= 0 && reaches(ring, box)) palette = wave;
  });
  return palette;
}
